import React, { forwardRef, useEffect, useState } from 'react';

import validate from '@vlabs/shared/validators';
import { Control } from '@vlabs/uikit';

import type { PopoverWithButtonProps } from '@vlabs/monokit/shared/ui/popovers';
import { PopoverWithButton } from '@vlabs/monokit/shared/ui/popovers';

import { useEventListFiltersTranslation } from '../../i18n';
import { useEventListFilters } from '../../useEventListFilters';
import st from '../popovers.module.sass';

const validateEventIds = validate.multipleUuid();
const validateExternalIds = validate.multipleExternalId();

const getIds = (value: string): string[] | undefined => {
  const ids = value
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

  if (ids.length === 0) return undefined;
  return ids;
};

function EventIdFilterPopover(
  props: PopoverWithButtonProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const { t } = useEventListFiltersTranslation();
  const [eventIds, setEventIds] = useState('');
  const [eventIdsError, setEventIdsError] = useState('');
  const [externalIds, setExternalIds] = useState('');
  const [externalIdsError, setExternalIdsError] = useState('');
  const { filters, updateFilters, updateFiltersDebounced } = useEventListFilters();

  const handleEventIdsChange = (value: string) => {
    setEventIds(value);

    const validationResult = validateEventIds(value);
    if (typeof validationResult === 'string') {
      setEventIdsError(validationResult);
      return;
    }

    setEventIdsError('');
    updateFiltersDebounced({ event_ids: getIds(value) });
  };

  const handleExternalIdsChange = (value: string) => {
    setExternalIds(value);

    const validationResult = validateExternalIds(value);
    if (typeof validationResult === 'string') {
      setExternalIdsError(validationResult);
      return;
    }

    setExternalIdsError('');
    updateFiltersDebounced({ external_ids: getIds(value) });
  };

  const reset = () => {
    setEventIds('');
    setEventIdsError('');
    setExternalIds('');
    setExternalIdsError('');
    updateFilters({ event_ids: undefined, external_ids: undefined });
  };

  const resetEventIds = () => {
    setEventIds('');
    setEventIdsError('');
    updateFilters({ event_ids: undefined });
  };

  const resetExternalIds = () => {
    setExternalIds('');
    setExternalIdsError('');
    updateFilters({ external_ids: undefined });
  };

  useEffect(() => {
    setEventIds(filters.event_ids?.join(',') ?? '');
  }, [filters.event_ids]);

  useEffect(() => {
    setExternalIds(filters.external_ids?.join(',') ?? '');
  }, [filters.external_ids]);

  return (
    <PopoverWithButton
      {...props}
      isButtonDisabled={
        filters.event_ids === undefined
        && filters.external_ids === undefined
      }
      onClickReset={reset}
      ref={ref}
    >
      <div className={st.DateInputWrapper}>
        <Control.Input
          errorMessage={eventIdsError}
          hasError={!!eventIdsError}
          isClearable
          label={t('event_ids')}
          onChangeValue={handleEventIdsChange}
          onClear={resetEventIds}
          value={eventIds}
        />
      </div>
      <div className={st.DateInputWrapper}>
        <Control.Input
          errorMessage={externalIdsError}
          hasError={!!externalIdsError}
          isClearable
          label={t('external_ids')}
          onChangeValue={handleExternalIdsChange}
          onClear={resetExternalIds}
          value={externalIds}
        />
      </div>
    </PopoverWithButton>
  );
}

const EventIdFilterPopoverWithRef = forwardRef(EventIdFilterPopover);
export { EventIdFilterPopoverWithRef as EventIdFilterPopover };
